import { FC, MouseEvent, useCallback, useMemo } from 'react';

import { push } from 'connected-react-router';
import { getAuctionTrlState } from 'utils/campaign';
import {
    CampaignCreationWizardStep,
    isCampaignCreationWizardHistoryState,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE } from 'utils/constants/app';
import { CampaignProductType } from 'utils/constants/campaign';

import auctionCampaignVacancyLinkButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_vacancy_link_button_click';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import styles from 'pages/AuctionBuilderPage/AuctionBuilderPage.module.less';

import { AuctionBuilder } from 'components/AuctionBuilder/AuctionBuilder';
import { PageContentHeader } from 'components/common/PageContentHeader/PageContentHeader';
import { VacancyLink } from 'components/common/VacancyLink/VacancyLink';
import { Header } from 'components/Header/Header';

import { auctionSelector } from 'store/models/auctionData';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: {
        create: 'AuctionBuilder.create.title',
        edit: 'AuctionBuilder.edit.title',
        restart: 'AuctionBuilder.restart.title',
    },
    breadcrumbsLink: 'AuctionBuilder.breadcrumbs.dashboard',
    wizardBreadcrumbs: {
        main: 'CampaignCreationWizard.breadcrumbs.main',
        creation: 'CampaignCreationWizard.breadcrumbs.creation',
        promotionType: 'CampaignCreationWizard.breadcrumbs.promotionType',
    },
};

export const AuctionBuilderPage: FC = () => {
    const isWizardFlow = useSelector((state) => isCampaignCreationWizardHistoryState(state.router.location.state));
    const vacancy = useSelector(auctionSelector.vacancy);
    const vacancyId = useSelector(auctionSelector.vacancyId);
    const auctionCampaign = useSelector(auctionSelector.auctionCampaign);

    const campaignId = auctionCampaign?.campaignId;
    const auctionTrlState = getAuctionTrlState(auctionCampaign);

    const trls = useTrls();
    const dispatch = useDispatch();
    const dashboardRoute = useDashboardRoute();
    const isAuctionCreate = auctionCampaign === null;
    const isWizardFlowEnabled = isAuctionCreate || isWizardFlow;
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const auctionWizardState = useMemo(
        () => ({
            vacancyId: vacancyId ? Number(vacancyId) : null,
            vacancyName: vacancy?.name,
            productType: CampaignProductType.SearchAuction,
        }),
        [vacancyId, vacancy]
    );

    const vacancyAdditionalContent = vacancy && (
        <VacancyLink
            dataQa="vacancy-link"
            vacancyId={vacancy.vacancyId}
            vacancyName={vacancy.name}
            onClick={() => auctionCampaignVacancyLinkButtonClick({ campaignId, vacancyId })}
        />
    );

    const handleMainCrumbClick = useCallback(
        (event: MouseEvent) => {
            event.preventDefault();
            if (!isAuctionCreate) {
                dispatch(push(dashboardRoute));
                return;
            }
            openBackAlert(() => dispatch(push(dashboardRoute)));
        },
        [dashboardRoute, dispatch, isAuctionCreate, openBackAlert]
    );

    const handleWizardBreadcrumbClick = useCallback(
        (activeStep: CampaignCreationWizardStep) => (event: MouseEvent) => {
            event.preventDefault();
            if (!isAuctionCreate) {
                goToWizardStep(activeStep, auctionWizardState);
                return;
            }

            openBackAlert(() => goToWizardStep(activeStep, auctionWizardState));
        },
        [goToWizardStep, isAuctionCreate, openBackAlert, auctionWizardState]
    );

    const breadcrumbsConfig = useMemo(() => {
        if (isWizardFlowEnabled) {
            return [
                {
                    to: dashboardRoute,
                    children: trls(TrlKeys.wizardBreadcrumbs.main),
                    onClick: handleMainCrumbClick,
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trls(TrlKeys.wizardBreadcrumbs.creation),
                    onClick: handleWizardBreadcrumbClick(CampaignCreationWizardStep.PromotionObject),
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trls(TrlKeys.wizardBreadcrumbs.promotionType),
                    onClick: handleWizardBreadcrumbClick(CampaignCreationWizardStep.PromotionType),
                },
                { to: '#', children: trls(TrlKeys.title[auctionTrlState]) },
            ];
        }

        return [
            {
                to: dashboardRoute,
                children: trls(TrlKeys.breadcrumbsLink),
            },
            { to: '#', children: trls(TrlKeys.title[auctionTrlState]) },
        ];
    }, [auctionTrlState, dashboardRoute, isWizardFlowEnabled, handleMainCrumbClick, handleWizardBreadcrumbClick, trls]);

    return (
        <div className={styles.wrapper}>
            <Header />
            <PageContentHeader
                breadcrumbsConfig={breadcrumbsConfig}
                title={trls(TrlKeys.title[auctionTrlState])}
                additionalContent={vacancyAdditionalContent}
                vSpacingBottom={24}
            />
            <AuctionBuilder />
            {isWizardFlowEnabled && isAuctionCreate && backAlert}
        </div>
    );
};
